import { computed, DestroyRef, Directive, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { cn } from '@cf/core';
import { CfDialogRef } from './dialog-ref';
import { CfDialogResult, getState } from './dialog.config';

@Directive({
  selector: '[cfDialogContent]',
  standalone: true,
  host: {
    role: 'dialog',
    '[attr.aria-describedby]': '"true"',
    '[attr.aria-labelledby]': '"true"',
    '[attr.data-state]': 'state()',
    '[class]': 'computedClass()',
  },
})
export class CfDialogContentDirective<C = unknown> {
  class = input<string>();
  hasPadding = input(true);
  rounded = input(true);

  computedClass = computed(() =>
    cn(
      'tw:relative tw:max-h-screen tw:gap-4 tw:bg-white tw:max-w-[calc(100dvw_-_2rem)] tw:duration-200 tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:overflow-auto tw:!transition-none tw:max-w-full tw:w-[calc(100vw_-_2rem)]',
      this.hasPadding() && 'tw:p-10 tw:max-sm:px-6',
      this.rounded() && 'tw:rounded-2xl tw:sm:rounded-lg',
      this.class(),
    ),
  );

  private readonly dialogRef = inject<CfDialogRef<C>>(CfDialogRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen = signal(true);

  readonly state = computed(() => getState(this.isOpen()));

  constructor() {
    this.dialogRef.closed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isOpen.set(false);
    });
  }

  /**
   * Closes the dialog with a specified result.
   *
   * @param result The result to be passed back when closing the dialog
   */
  close(result: CfDialogResult<C>): void {
    this.dialogRef.close(result);
  }

  /**
   * Dismisses the dialog without a result.
   */
  dismiss(): void {
    this.dialogRef.dismiss();
  }
}
